import { ScheduledDownload } from './../../models3/VideoCacheDownloader';
import { t } from 'ttag'
import _debug from 'debug'

import API from '../../models3/API'
import { Member, MemberNotifications, PassageNote, PassageNoteItem, PassageVideo } from '../../models3/ProjectModels'
import { IRoot } from '../../models3/Root'
import { displayError } from './Errors'
import { convertFromCanonicalTimestamp } from '../../models3/utils/dateServices'
import showNotificationSentToast from './ToastNotifications';

const log = _debug('sltt:PrepNotification')

type LinkTarget = PassageVideo | PassageNote | PassageNoteItem
type EventSummary = {
    symbol: string,
    english: string,
    /** native is from rt.uiLanguage (ttag), which might be english if not translated */
    native: string,
}

interface INotification {
    /** the entity that the link will navigate to */
    linkTarget: LinkTarget,
    /** actual textual content added, if any */
    text?: string,
    rt: IRoot,
    /** a summary of what happened, english and localized */
    eventSummary: EventSummary,
    /** creationDate for item */
    creationDate: string,
    /** date updated, if modified */
    updatedAt?: Date,
    /** if present, notification sent to only these members and it is sent immediately.
     * When not present, notification sent to all members at the scheduled time. */
    selectedMembers?: Member[],
}

const buildLinkText = (linkTarget: LinkTarget) => {
    const isPassageVideo = linkTarget instanceof PassageVideo
    const linkTextEn = isPassageVideo ? `Video` : 'Note'
    const linkTextLocalized = isPassageVideo ? t`Video` : t`Note`
    const linkText = buildLocalizedFollowedByEnglish(linkTextEn, linkTextLocalized)
    return linkText
}

const buildLocalizedFollowedByEnglish = (english: string, localized?: string) => {
    if (!localized || (english === localized)) {
        return english
    } else {
        return `${localized} (${english})`
    }
}

const buildEventSummaryText = (eventSummary: EventSummary) => {
    if (!eventSummary.native || (eventSummary.english === eventSummary.native)) {
        return { eventSummaryFirst: eventSummary.english, eventSummaryEnglish: '' }
    } else {
        return {
            eventSummaryFirst: eventSummary.native,
            eventSummaryEnglish: eventSummary.english,
        }
    }
}

const getTime = (linkTarget: LinkTarget, rt: IRoot) => {
    const { currentTime } = rt
    if (linkTarget instanceof PassageVideo && !linkTarget.isPatch) {
        return currentTime
    } else if (linkTarget instanceof PassageVideo && linkTarget.isPatch) {
        const segment = linkTarget.segments[0]
        !segment && console.error(`### no segment found for linkTarget`, { linkTarget })
        return segment?.time ?? 0
    } else if (linkTarget instanceof PassageNote) {
        return linkTarget.time
    } else {
        return currentTime
    }
}

export const prepNotification = ({ linkTarget, text, rt, eventSummary, creationDate, updatedAt, selectedMembers }: INotification) => {
    const { project, note, passage, username } = rt

    let emailsToNotify: string[]
    let scheduledSendDate: string

    if (selectedMembers) {
        emailsToNotify = selectedMembers.map(member => member.email)
        scheduledSendDate = ''  // send immediately''
    } else {
        // Remove currently logged in member's email from emailsToNotify
        emailsToNotify = project.members
            .filter(member => member.email !== username &&
                member.notifications === MemberNotifications.HOURLY)
            .map(member => member.email)
        if (emailsToNotify.length === 0) {
            log(`no emailsToNotify`)
            return
        }
        
        // Currently this is Now, in the future it will be 24 hours from the creation date
        scheduledSendDate = (new Date()).toISOString()
    }

    const noteDescription = note?.description?.trim() ?? null
    const unknownName = '???'
    const passageName = passage?.name ?? unknownName
    const portionName = rt?.portion?.name ?? unknownName
    
    const formattedCreationDate = rt.dateFormatter.format(convertFromCanonicalTimestamp(creationDate))
    const utcOffset = buildUTCOffset()
    const textContent = text ?? ''
    const link = createLink({ rt, linkTarget })
    const isPassageVideo = linkTarget instanceof PassageVideo
    const isPassageNote = linkTarget instanceof PassageNote
    const linkIcon = isPassageVideo ? '▶' : '🗨'
    const linkText = buildLinkText(linkTarget)
    const { eventSummaryFirst, eventSummaryEnglish } = buildEventSummaryText(eventSummary)
    const noteContext = isPassageNote ? noteDescription ? ` → ${noteDescription.slice(0, 20)}...` : '' : ''
    const primaryContext = `${portionName} → ${passageName}`
    const time = getTime(linkTarget, rt)
    const user = rt.project.creatorName(rt.username)
    const mailto = `mailto:${rt.username}`
    const formattedUpdateDate = updatedAt && rt.dateFormatter.format(updatedAt)
    const formattedUpdateDateHtml = formattedUpdateDate && formattedUpdateDate !== formattedCreationDate ? `→<br>${formattedUpdateDate} ` : ''
    const emailBody = `<p style="font-family: Arial, sans-serif; font-size: 16px; color: #333; line-height: 1.5; margin-bottom: 0px;">
        <strong>
            <a href="${link}" target="_blank" style="color: #0073e6;"><span style="display: inline-block;">${linkIcon}&nbsp;</span>${linkText}</a>
        </strong> -
        <span style="color: #555;">
            ${primaryContext} <span style="color: #777; font-size: 14px;">@ ${time.toFixed(2)}</span> ${noteContext}
        </span>
        </p>
        <p><span style="font-size: 15px; color: #555; margin-left: 10px;">${eventSummary.symbol ? eventSummary.symbol + '&nbsp;' : ''}<em>${eventSummaryFirst}</em></span>
        ${eventSummaryEnglish && `<br><span style="font-size: 15px; color: #555; margin-left: 10px;"><em>${eventSummaryEnglish}</em></span></p>`}
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="font-size: 14px; color: #333; margin-left: 20px">
        <tr>
            <td>
                <table role="presentation" cellspacing="0" cellpadding="0">
                    <tr>
                        <td style="padding: 0 0 0 10px; font-size: 14px; color: #333;">
                            <em><a href="${mailto}" style="color: #0073e6; text-decoration: none;">${user}</a></em> <span style="margin-left: 5px;  color: #ccc;"><!-- ${rt.uiLanguage || 'en'} --></span>
                        </td>
                        <td style="padding: 0 0 0 10px; font-size: 14px; color: #333;">
                            ${formattedCreationDate} ${formattedUpdateDateHtml}UTC${utcOffset}
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
            <tr style="height: 10px;"><td>&nbsp;</td></tr> <!-- Spacer row -->
        <tr>
            <td style="border-left: 4px solid #cccccc; padding-left: 10px;">
                ${textContent}
            </td>
        </tr>
        </table><br/>`

    let emailSource = project.displayName
    if (scheduledSendDate == '') {
        const userName = user.split('@')[0]
        emailSource += ' ' + t`(from: ${userName})` 
    }
    
    API.addNotification(project.name, emailSource, emailsToNotify, scheduledSendDate, emailBody.replace(/\n/g, ''))
        .then(() => {
            if (scheduledSendDate == '') {
                showNotificationSentToast(t`Notification sent`)
            }
        })
        .catch(displayError)   
    log(`emailsToNotify`, {
        input: { linkTarget, eventSummary, text, creationDate, updatedAt },
        notification: { emailsToNotify, emailBody, projectName: project.name, scheduledSendDate },
        emailBodyParts: {
            link, linkIcon, linkText, eventSummaryFirst, eventSummaryEnglish,
            portionName, passageName, noteContext, primaryContext, time,
            mailto, user, linkTarget, noteDescription,
            formattedCreationDate, formattedUpdateDate, utcOffset,
            textContent,
        }
    })
}

function buildUTCOffset() {
    const offset = new Date().getTimezoneOffset()
    const hours = Math.floor(Math.abs(offset) / 60)
    const minutes = Math.abs(offset % 60)
    const sign = offset > 0 ? '-' : '+'
    return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

const createLink = ({ rt, linkTarget }: { rt: IRoot, linkTarget: LinkTarget }) => {
    const { currentTime, project, passageVideo } = rt
    const _id = linkTarget?._id || passageVideo?._id
    // use sltt-bible.net as the base url since it's compatible with most clients
    // otherwise other emailed team members may not be able to open the link
    const link = `https://sltt-bible.net/#/index.html?project=${project.name}&id=${_id}&time=${currentTime}`
    return link
}